import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/react-hooks";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import PlusIcon from "@iconify/icons-mdi/plus";
import { InlineIcon } from "@iconify/react";
import { MY_HR_GET_MODULE_ROLES } from "my-hr/graphql";
import { OcErrorBoundary } from "oc/components";
import { useOcClientState, useOcPolling } from "oc/hooks";
import getModuleIcon from "oc/utils/get-modul-icon";

interface MyHrAddUserRoleProps {
  contract: any; // Replace 'any' with the actual contract type if available
  onAddRole: (params: {
    contract: string;
    module: string;
    role: string;
  }) => void;
  roles: any[]; // Replace 'any' with the actual role type if available
  isMobile: boolean;
}

export const MyHrAddUserRole: React.FC<MyHrAddUserRoleProps> = ({
  contract,
  onAddRole,
  roles,
  isMobile,
}) => {
  const [showAddRoleCollapse, setShowAddRoleCollapse] =
    useOcClientState<boolean>(`showAddRoleCollapse${contract?.code}`, false);
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useOcClientState<string>(
    "selectedModule",
    "MyHR",
  );

  const [selectedRole, setSelectedRole] = useOcClientState<string>(
    "selectedRole",
    "",
  );

  const handleChange = (
    event: SelectChangeEvent<string>,
    // akár így is megadhatod, ha a TS-kötöttség miatt kell:
    _child: React.ReactNode,
  ) => {
    setSelectedProduct(event.target.value);
  };

  useEffect(() => {
    return () => {
      setShowAddRoleCollapse(false);
      setSelectedProduct("MyHR");
      setSelectedRole("");
    };
    // eslint-disable-next-line
  }, []);

  const { data } = useQuery(MY_HR_GET_MODULE_ROLES, {
    fetchPolicy: "cache-and-network",
    pollInterval: useOcPolling(1000 * 60),
  });

  let selectedModule = data?.moduleRoles?.find(
    (item: any) => item.module === selectedProduct,
  );

  let availableRoles = selectedModule?.roles || [];

  const handleCancel = () => {
    setShowAddRoleCollapse(false);
    setSelectedProduct("MyHR");
    setSelectedRole("");
  };

  const handleSelectRole = (role: string) => {
    setSelectedRole(role);
  };

  const handleAddRole = () => {
    let module = selectedModule?.module;
    let role = selectedRole;
    onAddRole({ contract: contract?.code, module, role });
    setShowAddRoleCollapse(false);
    setSelectedProduct("MyHR");
    setSelectedRole("");
  };

  const excludedCodes = ["0", "5", "6", "7", "8", "9", "V", "Y", "N", "O", "X"];

  const isDisabled = ({
    contract,
    module,
    role,
    typeOfContract,
  }: {
    contract: string;
    module: string;
    role: string;
    typeOfContract: string;
  }) => {
    let isExist = roles?.find(
      (item) =>
        item.contract === contract &&
        item.product === module &&
        item.role === role,
    );
    if (isExist) return true;
    if (typeOfContract === "Z" && module === "MyLeave" && role === "user") {
      return true;
    }
    return false;
  };

  useEffect(() => {
    setSelectedRole("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  return (
    <OcErrorBoundary>
      <Box>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
          paddingTop="9px"
          paddingBottom="36px"
        >
          <Button
            id="add-role"
            name="showDialog"
            variant="contained"
            color="primary"
            startIcon={<InlineIcon icon={PlusIcon} />}
            disabled={showAddRoleCollapse}
            onClick={() => setShowAddRoleCollapse(true)}
            disableElevation
          >
            {t("MY_HR_ADD_ROLE")}
          </Button>
        </Box>
        <Collapse in={showAddRoleCollapse} timeout="auto" unmountOnExit>
          <Box display="flex" justifyContent="center">
            <Card
              variant="outlined"
              style={{
                borderRadius: "4px",
                borderColor: "white",
                overflow: "visible",
                marginBottom: "16px",
                backgroundColor: "#F1F3F4",
                width: "728px",
              }}
            >
              <Box padding="16px" display="flex" flexDirection="column">
                <Box
                  fontSize="20px"
                  letterSpacing="0.15px"
                  padding="15px 0px"
                  paddingBottom="30px"
                >
                  {t("MY_HR_ADD_ROLE")}
                </Box>
                <Box
                  display="flex"
                  flexDirection={isMobile ? "column" : "row"}
                  paddingBottom="8px"
                >
                  <Box width={isMobile ? "100%" : "50%"} paddingRight="8px">
                    <FormControl
                      variant="outlined"
                      fullWidth
                      color="secondary"
                      style={{ backgroundColor: "white" }}
                    >
                      <InputLabel>{t("OC_MODULE")}</InputLabel>
                      <Select
                        labelId="demo-simple-outlined-label"
                        id="module-select"
                        value={selectedProduct}
                        onChange={handleChange}
                        label={t("OC_MODULE")}
                      >
                        {data?.moduleRoles?.map((module: any) => {
                          const isMyLeaveModule = module.module === "MyLeave";
                          const isDisabled =
                            excludedCodes?.includes(
                              contract?.employmentType?.code,
                            ) &&
                            isMyLeaveModule &&
                            contract?.typeOfContract?.code !== "Z";
                          return (
                            <MenuItem
                              disabled={isDisabled}
                              key={module.module}
                              value={module.module}
                            >
                              <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                              >
                                {getModuleIcon(module.module)}
                                <Typography
                                  variant="body1"
                                  style={{
                                    marginLeft: "22px",
                                  }}
                                >
                                  {module.module}
                                </Typography>
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                  {isMobile && <Box height="16px" />}
                  <Box
                    width={isMobile ? "100%" : "50%"}
                    paddingLeft={isMobile ? "0px" : "8px"}
                  >
                    <ButtonGroup
                      orientation="vertical"
                      aria-label="vertical outlined primary button group"
                      fullWidth
                      style={{
                        color: "#E3E5E5",
                      }}
                      variant="contained"
                      disableElevation
                    >
                      {availableRoles?.map((role: string) => {
                        let disabled = isDisabled({
                          contract: contract?.code,
                          module: selectedModule?.module,
                          role: role,
                          typeOfContract: contract?.typeOfContract?.code,
                        });
                        return (
                          <Button
                            id={role}
                            key={role}
                            onClick={() => handleSelectRole(role)}
                            disableElevation
                            color="secondary"
                            variant="text"
                            disabled={disabled}
                            style={
                              role === selectedRole
                                ? {
                                    backgroundColor: "#cc0033",
                                    color: "white",
                                  }
                                : {
                                    backgroundColor: "white",
                                    color: disabled ? "#C6CACC" : "#212121",
                                  }
                            }
                          >
                            {t(`MY_HR_ROLE_${role.toUpperCase()}`)}
                          </Button>
                        );
                      })}
                    </ButtonGroup>
                  </Box>
                </Box>
                <Box
                  display="flex"
                  flexDirection={isMobile ? "column-reverse" : "row"}
                  justifyContent="flex-end"
                  padding={isMobile ? "0px" : "10px 0px 0px 0px"}
                >
                  <Button
                    id="my-hr-add-user-role-cancel-button"
                    onClick={handleCancel}
                    color="secondary"
                    variant="text"
                  >
                    {t("OC_CANCEL")}
                  </Button>
                  {isMobile && <Box height="16px" />}
                  <Button
                    id="my-hr-add-user-role-button"
                    variant="contained"
                    color="primary"
                    disableElevation
                    style={{ marginLeft: isMobile ? "0px" : "16px" }}
                    disabled={!selectedRole}
                    onClick={handleAddRole}
                  >
                    {t("OC_ADD")}
                  </Button>
                  {isMobile && <Box height="16px" />}
                </Box>
              </Box>
            </Card>
          </Box>
        </Collapse>
      </Box>
    </OcErrorBoundary>
  );
};

export default MyHrAddUserRole;
